import type { Type } from '@angular/core';
import type { AuroraWidgetDef, AuroraWidgetLink, AuroraWidgetSpan } from '../aurora-widget-def';
import type { WidgetCategory } from '../widget-category';
import type { WidgetDataSource } from '../widget-data-source';
import type { ChartSeries, ChartType } from './chart-widget.component';

// Re-export types so consumers can reach them via @aurora without pulling the component.
export type { ChartSeries, ChartType } from './chart-widget.component';

// ─── ChartWidgetConfig ────────────────────────────────────────────────────────

interface ChartWidgetConfig {
  chartType: ChartType;
  source: WidgetDataSource<ChartSeries[]>;
  xAxisData?: string[];
  options?: import('echarts/core').EChartsCoreOption;
  /**
   * When `true`, the chart component translates each data-point name using
   * `i18nLabelKeyPrefix + name` as the full i18n key, with fallback to the raw
   * name when no translation is found. Default: `false` (names pass through as-is).
   *
   * Use this for charts whose data-point names are enum codes that already have
   * translations in a Transloco scope (e.g. `iam.account.Type.SERVICE`).
   * Data mappers remain pure — they continue emitting raw enum codes.
   */
  i18nLabels?: boolean;
  /**
   * Scope-qualified prefix used to build the full i18n key for each data-point
   * name when `i18nLabels` is `true` (e.g. `'o-auth.client.GrantType.'`).
   * The composed key is `i18nLabelKeyPrefix + name` (e.g.
   * `'o-auth.client.GrantType.AUTHORIZATION_CODE'`).
   * Required when `i18nLabels` is `true`.
   */
  i18nLabelKeyPrefix?: string;
}

// ─── defineChartWidget ────────────────────────────────────────────────────────

/** Default span for chart widgets: medium-large tile. */
const CHART_DEFAULT_SPAN: AuroraWidgetSpan = {
  minCols: 3,
  maxCols: 6,
  minRows: 3,
  maxRows: 6,
  defaultCols: 4,
  defaultRows: 3,
};

export interface DefineChartWidgetConfig extends ChartWidgetConfig {
  id: string;
  translation: string;
  iconUrl: string;
  description?: string;
  permission?: string | string[];
  category?: WidgetCategory;
  span?: AuroraWidgetSpan;
  link?: AuroraWidgetLink;
}

/**
 * Factory that converts a typed `DefineChartWidgetConfig` into an
 * `AuroraWidgetDef`. The archetype config fields are placed in `inputs`
 * for forwarding via `[ngComponentOutletInputs]`.
 */
export function defineChartWidget(config: DefineChartWidgetConfig): AuroraWidgetDef {
  const { id, translation, iconUrl, description, permission, category, span, link } = config;
  const { chartType, source, xAxisData, options, i18nLabels, i18nLabelKeyPrefix } = config;

  return {
    id,
    translation,
    iconUrl,
    description,
    permission,
    category,
    link,
    span: span ?? CHART_DEFAULT_SPAN,
    inputs: {
      chartType,
      source,
      ...(xAxisData !== undefined && { xAxisData }),
      ...(options !== undefined && { options }),
      ...(i18nLabels !== undefined && { i18nLabels }),
      ...(i18nLabelKeyPrefix !== undefined && { i18nLabelKeyPrefix }),
    },
    component: (): Promise<Type<unknown>> =>
      import('./chart-widget.component').then((m) => m.ChartWidgetComponent as Type<unknown>),
  };
}
